import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ymd } from '@/components/ui/PeriodPicker';
import { GST_STATE_MAP } from '@/lib/validation';
import { tradeCalc } from '@/lib/tradeCalc';
import { purchasesYtd, tradeSetup, type TradeInput } from '@/api/accountsBooks';
import { ChipTag, DateField, F, FormFooter, FormTitle, Msgs, Posting, ReadBox, Sec } from './voucherFormParts';
import { curBal, dd, inr } from './voucherFormat';
import { Grid, ItemsTable, NewRefTable, PartyDetails } from './tradeParts';
import { addDays, fromItems, newLine, useSaveTrade, type Line } from './tradeHelpers';
import type { FormProps } from './useVoucherForm';

/* Purchase — laid out like Tally's Purchase voucher (F9, item invoice), as the prototype:
   Supplier invoice no. / date → Party A/c → Purchase ledger → items (qty × rate, GST %) →
   Order & Receipt Details → TDS (sec 1031 / old 194Q) → Bill-wise Details (New Ref = this
   bill) → Narration → Posting. */

const PD_FIELDS: [string, string, number?, string?][] = [
  ['order', 'Order No.'], ['orderDate', 'Order date', undefined, 'date'], ['receiptNote', 'Receipt Note No.'], ['terms', 'Mode / Terms of payment'],
  ['dispatchDoc', 'Dispatch Doc No.'], ['through', 'Dispatched through'], ['vehicle', 'Motor vehicle no.'],
];

interface State {
  date: string; party: number | ''; ledger: number | ''; billNo: string; billDate: string; days: number; roundOff: boolean; tds: boolean;
  lines: Line[]; narr: string; pd: Record<string, string>;
}

export function PurchaseForm({ voucher, ledgers, nextNo, onClose }: FormProps) {
  const setupQ = useQuery({ queryKey: ['acc-books', 'trade-setup'], queryFn: tradeSetup });
  const home = setupQ.data?.stateCode ?? null;
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const suppliers = ledgers.filter((l) => l.role === 'C');
  const purLedgers = ledgers.filter((l) => l.group === 'Purchase Accounts' && !/Return/i.test(l.name));
  const usualLedger = (partyId: number | '') => {
    const p = partyId === '' ? undefined : byId.get(partyId);
    const intra = !(home && p?.stateCode && home !== p.stateCode);
    return purLedgers.find((l) => l.name === (intra ? 'Purchase – Local' : 'Purchase – Interstate'))?.id ?? purLedgers[0]?.id ?? '';
  };
  const today = ymd(new Date());
  const d = voucher?.details;

  const [s, setS] = useState<State>(() => (voucher && d
    ? {
      date: voucher.date, party: d.partyLedgerId, ledger: d.ledgerId, billNo: d.billNo ?? '', billDate: d.billDate ?? voucher.date, days: d.creditDays,
      roundOff: d.roundOff, tds: !!d.tds, lines: fromItems(voucher.items), narr: voucher.narration ?? '', pd: d.pd ?? {},
    }
    : { date: today, party: '', ledger: '', billNo: '', billDate: today, days: 45, roundOff: true, tds: true, lines: [newLine()], narr: '', pd: {} }));
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));

  const party = s.party === '' ? undefined : byId.get(s.party);
  const ledger = s.ledger === '' ? undefined : byId.get(s.ledger);
  const intra = !(home && party?.stateCode && home !== party.stateCode);
  const registered = !!party?.gstin;
  // This supplier's purchases this year so far (without this voucher, when altering) — for TDS 1031.
  const ytdQ = useQuery({
    queryKey: ['acc-books', 'purchases-ytd', s.party, s.date, voucher?.id ?? 0],
    queryFn: () => purchasesYtd(s.party as number, s.date, voucher?.id),
    enabled: s.party !== '' && !!s.date && s.tds,
  });
  const c = tradeCalc('Purchase', s.lines.map((l) => ({ qty: l.qty, rate: l.rate, disc: l.disc, gst: l.gst })), {
    intra, registered, roundOff: s.roundOff, tds: { on: s.tds, before: ytdQ.data?.before ?? 0 },
  });
  const due = addDays(s.billDate, s.days);
  const stateName = party?.stateCode ? GST_STATE_MAP[party.stateCode] ?? party.stateCode : '';

  const block: string[] = [];
  const warn: string[] = [];
  const valid = s.lines.filter((l) => l.qty > 0 && l.rate > 0);
  if (!s.date) block.push('Enter the date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!party) block.push('Choose the supplier (Party A/c name).');
  if (!ledger) block.push('Choose the Purchase ledger.');
  if (!s.billNo.trim()) block.push('Enter the supplier invoice no.');
  if (s.billDate > s.date) block.push('Supplier invoice date cannot be after the voucher date.');
  if (!valid.length) block.push('Add at least one item with quantity and rate.');
  if (valid.some((l) => !l.name.trim())) block.push('Every item needs its name.');
  if (valid.some((l) => !l.hsn.trim())) block.push('Every item needs an HSN code.');
  if (valid.some((l) => l.disc < 0 || l.disc > 100)) block.push('Discount must be between 0 and 100%.');
  if (party && !registered) warn.push(`${party.name} is not GST-registered — no input GST on this bill.`);
  if (party && ledger && ledger.id !== usualLedger(s.party)) warn.push(`${party.name} is ${intra ? 'in' : 'outside'} ${GST_STATE_MAP[home ?? ''] ?? 'the company’s state'} — usually "${byId.get(usualLedger(s.party) as number)?.name}".`);

  const post = [
    { l: ledger?.name ?? '', a: c.taxable }, { l: 'Input CGST', a: c.cgst }, { l: 'Input SGST', a: c.sgst }, { l: 'Input IGST', a: c.igst },
    { l: party?.name ?? '', a: -c.payable, sub: [`New Ref ${s.billNo.trim() || '(bill no.)'} · due ${dd(due)} · ${inr(c.payable)}`] },
    ...(c.tds ? [{ l: 'TDS Payable – 1031', a: -c.tds }] : []), { l: 'Round Off', a: c.ro },
  ];
  const build = (): TradeInput => ({
    date: s.date, partyLedgerId: s.party as number, ledgerId: s.ledger as number, billNo: s.billNo.trim(), billDate: s.billDate,
    creditDays: s.days, roundOff: s.roundOff, tds: s.tds,
    items: valid.map(({ key: _k, ...l }) => ({ ...l, name: l.name.trim(), hsn: l.hsn.trim() })),
    narration: s.narr.trim(), pd: s.pd,
  });
  const save = useSaveTrade('Purchase', voucher, onClose);

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={<FormTitle title="Purchase" no={voucher?.voucherNo ?? nextNo} chip={<ChipTag tone="o">Goods in</ChipTag>} />}
      footer={
        <FormFooter left={<>Payable to supplier <b>{inr(c.payable)}</b></>} version={voucher ? voucher.version + 1 : undefined}
          saveLabel="Save Purchase" blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />
      }
    >
      <Grid>
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v })} />
        <F label="Supplier invoice no. *"><Input className="font-mono" maxLength={60} value={s.billNo} onChange={(e) => set({ billNo: e.target.value })} /></F>
        <F label="Supplier invoice date"><Input type="date" max={today} value={s.billDate} onChange={(e) => set({ billDate: e.target.value })} /></F>
        <F label="Credit days (from invoice date)"><Input type="number" min={0} className="text-right font-mono" value={s.days} onChange={(e) => set({ days: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></F>
        <F label="Party A/c name *" className="col-span-2">
          <SearchableSelect
            options={suppliers.map((p) => ({ value: p.id, label: p.name, sublabel: [p.partyCode, p.gstin ?? 'Unregistered'].filter(Boolean).join(' · ') }))}
            value={s.party} placeholder="Supplier"
            onChange={(v) => { const id = v === '' ? '' : Number(v); set({ party: id, ledger: usualLedger(id) }); }}
          />
        </F>
        <F label="Cur Bal"><ReadBox value={curBal(party)} /></F>
        <F label="GSTIN / State"><Input disabled className="font-mono" value={party ? `${party.gstin || 'Unregistered'} · ${stateName}` : ''} /></F>
        <F label="Purchase ledger *" className="col-span-2">
          <Select value={s.ledger} onChange={(e) => set({ ledger: Number(e.target.value) })}>
            {s.ledger === '' && <option value="">— choose —</option>}
            {purLedgers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </F>
      </Grid>

      <Sec>Items</Sec>
      <ItemsTable
        lines={s.lines} amounts={c.lines.map((l) => l.amt)} setLines={(lines) => set({ lines })}
        taxRows={[['Input CGST', c.cgst], ['Input SGST', c.sgst], ['Input IGST', c.igst]]} ro={c.ro} total={c.R}
        rateOf={(it) => it.purchaseRate}
      />
      <label className="ml-1 mt-2 inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
        <input type="checkbox" checked={s.roundOff} onChange={(e) => set({ roundOff: e.target.checked })} /> Round off the total to the rupee (Round Off ledger)
      </label>

      <PartyDetails title="Order & Receipt Details" fields={PD_FIELDS} pd={s.pd} setPd={(pd) => set({ pd })} />

      <Sec>TDS — sec 1031 (old 194Q), purchase of goods</Sec>
      <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
        <input type="checkbox" checked={s.tds} onChange={(e) => set({ tds: e.target.checked })} />
        Deduct TDS (sec 1031 / old 194Q) when this supplier’s purchases this year cross ₹50 L
      </label>
      {s.tds && party && (
        <div className="mt-1 text-xs text-[var(--text-muted)]">
          {ytdQ.isLoading ? 'Checking this year’s purchases…' : c.tdsReason}
          {c.tds ? <> — <b className="text-[var(--text-primary)]">TDS {inr(c.tds)}</b> on {inr(c.tdsBase)} @ 0.1%</> : ' — no TDS on this bill'}
        </div>
      )}

      <Sec>Bill-wise Details{party ? ` for ${party.name}` : ''}</Sec>
      <NewRefTable name={s.billNo.trim()} billDate={s.billDate} due={due} days={s.days} amount={c.payable} side="Cr" />

      <Msgs block={block} warn={warn} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder={`Purchase bill ${s.billNo.trim() || '…'} – ${party?.name ?? ''}`} onChange={(e) => set({ narr: e.target.value })} />
      </F>
      <Sec>Posting</Sec>
      <Posting lines={post} />
    </Modal>
  );
}
